import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { faqRepository } from '@/data/faqs/repositories/faq_repository_impl'
import { CreateFaqUseCase } from '@/domain/faqs/usecases/create_faq.usecase'
import { getAxiosLikeErrorMessage } from '@/services/apiErrors'
import { logError } from '@/utils/logger'
import type { FaqFormErrors } from '../models/faqs_ui_state'

const defaultCreateFaq = new CreateFaqUseCase(faqRepository)

export interface UseAddFaqViewModelDeps {
  createFaq?: CreateFaqUseCase
}

export function useAddFaqViewModel(deps: UseAddFaqViewModelDeps = {}) {
  const navigate = useNavigate()
  const createFaq = deps.createFaq ?? defaultCreateFaq

  const [question, setQuestion] = useState('')
  const [answer, setAnswer] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [errors, setErrors] = useState<FaqFormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const submit = useCallback(async () => {
    setIsSubmitting(true)
    const newErrors: FaqFormErrors = {}

    if (!question.trim()) {
      newErrors.question = 'Question is required'
    }
    if (!answer.trim()) {
      newErrors.answer = 'Answer is required'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      setIsSubmitting(false)
      return
    }

    try {
      await createFaq.execute({
        question: question.trim(),
        answer: answer.trim(),
        status: isActive ? 'active' : 'inactive',
      })
      navigate('/admin/cms/faq')
    } catch (error: unknown) {
      logError('Failed to create FAQ:', error)
      alert(getAxiosLikeErrorMessage(error, 'Failed to create FAQ. Please try again.'))
    } finally {
      setIsSubmitting(false)
    }
  }, [answer, createFaq, isActive, navigate, question])

  const cancel = useCallback(() => {
    navigate('/admin/cms/faq')
  }, [navigate])

  return {
    form: {
      question,
      answer,
      isActive,
      errors,
      isSubmitting,
    },
    actions: {
      setQuestion,
      setAnswer,
      setIsActive,
      submit,
      cancel,
      clearFieldError: (field: keyof FaqFormErrors) =>
        setErrors(prev => ({ ...prev, [field]: undefined })),
    },
  }
}
